import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useTranslation } from 'react-i18next';
import Button from 'src/component/Button';
import { Store } from 'src/store';
import formatPrice from 'src/util/price/formatPrice';
// eslint-disable-next-line max-len
import sendRequestDesignDataPostMessageToMultiSizeAddToBasketOperation from 'src/store/app/operation/sendRequestDesignDataPostMessageToMultiSizeAddToBasketOperation';
import { setMultiSizePendingItemsAction } from 'src/store/multisize/slice';
import useMultiSizeRows from './useMultiSizeRows';
import styles from './index.module.scss';

/**
 * OP-39782 — multi-size quantity drawer for the apparel editor.
 *
 * Opens on Add To Basket, lets the customer pick a quantity per size, and
 * commits all non-zero sizes as one grouped basket line via /design/batch-add.
 * All derived state (size resolution, pricing, quantities) lives in
 * useMultiSizeRows; this component is presentation + the commit trigger.
 */

interface Props {
  onClose: () => void;
}

const MultiSizeDrawer = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<Store, void, AnyAction>>();
  const isMobile = useSelector((s: Store) => s.app.isMobile);
  const {
    rows, totalUnits, totalValue, totalInitial, knownPrices, currencyId, priceFormat, setQty, buildItems,
  } = useMultiSizeRows();

  const showPrice = knownPrices && totalValue > 0 && !!currencyId && !!priceFormat;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onAdd = () => {
    const items = buildItems();
    if (items.length === 0) {
      return;
    }

    // Stash the selection, then run the same pre-commit round-trip as single-add
    // (request latest design data → save → guards). The host.addToBasketMultiSize
    // callback then commits via /design/batch-add.
    dispatch(setMultiSizePendingItemsAction(items));
    dispatch(sendRequestDesignDataPostMessageToMultiSizeAddToBasketOperation());
    onClose();
  };

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.drawer} data-mobile={isMobile} role="dialog" aria-label="Size and quantity">
        <div className={styles.header}>
          <span className={styles.title}>Size and quantity</span>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={styles.rows}>
          {rows.map((r) => (
            <div className={styles.row} data-active={r.qty > 0} data-unavailable={!r.available} key={r.value}>
              <span className={styles.size}>{r.name}</span>
              {r.available ? (
                <div className={styles.stepper}>
                  <button
                    className={styles.step}
                    disabled={r.qty <= r.min}
                    aria-label={`Decrease quantity, size ${r.name}`}
                    onClick={() => setQty(r.value, r.qty - 1)}
                  >
                    −
                  </button>
                  <span className={styles.qty} aria-live="polite">{r.qty}</span>
                  <button
                    className={styles.step}
                    aria-label={`Increase quantity, size ${r.name}`}
                    onClick={() => setQty(r.value, r.qty + 1)}
                  >
                    +
                  </button>
                </div>
              ) : (
                <span className={styles.unavailable}>Unavailable</span>
              )}
            </div>
          ))}
          {rows.length === 0 && <div className={styles.empty}>No size selector found for this product.</div>}
        </div>

        <div className={styles.footer}>
          <div className={styles.total}>
            <span>{`Total · ${totalUnits} item${totalUnits === 1 ? '' : 's'}`}</span>
            <span aria-live="polite">
              {showPrice ? (
                <>
                  {totalInitial > totalValue && (
                    <span className={styles.strike}>{formatPrice(totalInitial, priceFormat || '', currencyId || '')}</span>
                  )}
                  <span className={styles.final}>{formatPrice(totalValue, priceFormat || '', currencyId || '')}</span>
                </>
              ) : '—'}
            </span>
          </div>
          <Button
            primary
            withPadding
            className={styles.cta}
            disabled={totalUnits === 0}
            onClick={onAdd}
            text={t('opeh_top-bar_add-to-basket')}
          />
        </div>
      </div>
    </>
  );
};

export default MultiSizeDrawer;
